import { ethers } from 'ethers';
import { CONFIG } from '../config';

class ContractService {
  constructor() {
    this.contract = null;
    this.provider = null;
    this.signer = null;
  }

  async initialize() {
    if (!window.ethereum) {
      throw new Error('MetaMask not installed');
    }

    this.provider = new ethers.BrowserProvider(window.ethereum);
    this.signer = await this.provider.getSigner();
    this.contract = new ethers.Contract(CONFIG.CONTRACT_ADDRESS, CONFIG.CONTRACT_ABI, this.signer);
    
    return this.contract;
  }

  async getReadOnlyContract() {
    if (!window.ethereum) {
      throw new Error('MetaMask not installed');
    }

    const provider = new ethers.BrowserProvider(window.ethereum);
    return new ethers.Contract(CONFIG.CONTRACT_ADDRESS, CONFIG.CONTRACT_ABI, provider);
  }

  async registerPolicy(policyId, metadataCid, heartbeatInterval, requiredQuorum, beneficiaries) {
    if (!this.contract) await this.initialize();
    
    const tx = await this.contract.registerPolicy(
      policyId,
      metadataCid,
      heartbeatInterval,
      requiredQuorum,
      beneficiaries
    );
    
    return await tx.wait();
  }

  async heartbeat(policyId) {
    if (!this.contract) await this.initialize();
    
    const tx = await this.contract.heartbeat(policyId);
    return await tx.wait();
  }

  async releasePolicy(policyId) {
    if (!this.contract) await this.initialize();
    
    const tx = await this.contract.releasePolicy(policyId);
    return await tx.wait();
  }

  async voteForRelease(policyId) {
    if (!this.contract) await this.initialize();
    
    const tx = await this.contract.voteForRelease(policyId);
    return await tx.wait();
  }

  async revokePolicy(policyId) {
    if (!this.contract) await this.initialize();
    
    const tx = await this.contract.revokePolicy(policyId);
    return await tx.wait();
  }

  async getPolicyView(policyId) {
    const contract = await this.getReadOnlyContract();
    return await contract.getPolicyView(policyId);
  }

  async getUserPolicies(userAddress) {
    const contract = await this.getReadOnlyContract();
    return await contract.getUserPolicies(userAddress);
  }

  async getBeneficiaries(policyId) {
    const contract = await this.getReadOnlyContract();
    return await contract.getBeneficiaries(policyId);
  }

  async hasVoted(policyId, beneficiary) {
    const contract = await this.getReadOnlyContract();
    return await contract.hasVoted(policyId, beneficiary);
  }

  async isExpired(policyId) {
    const contract = await this.getReadOnlyContract();
    return await contract.isExpired(policyId);
  }

  async getSignerAddress() {
    if (!this.signer) await this.initialize();
    return await this.signer.getAddress();
  }

  async getTotalPolicies() {
    const contract = await this.getReadOnlyContract();
    return await contract.totalPolicies();
  }

  // Helper method to get all policies where address is a beneficiary
  // Note: This is a simplified version. In production, you'd want indexed events
  async getAllBeneficiaryPolicies(beneficiaryAddress) {
    // This is a placeholder that returns an empty array
    // In a real implementation, you would:
    // 1. Listen to PolicyRegistered events and filter by beneficiary
    // 2. Or have a contract mapping of beneficiary -> policyIds
    // For now, we'll return empty array and rely on users knowing their policy IDs
    return [];
  }
}

export const contractService = new ContractService();
